/**
 * Analytics Tracker — fire-and-forget client-side beacon.
 *
 * Captures:
 *   • session_start  (implicit on first event from a new sessionStorage id)
 *   • pageview       (on every route change via usePageviewTracker hook)
 *   • search         (explicit trackSearch call from search forms / SearchResults)
 *   • book_step      (funnel steps: passenger_form, review, payment_init)
 *   • book_confirmed (PaymentSuccess — terminal funnel step)
 *
 * Reliability:
 *   • Events buffered in memory and flushed every 4s OR on visibilitychange='hidden'
 *   • navigator.sendBeacon used on unload (browsers guarantee delivery)
 *   • Failures are swallowed — never blocks the UX
 *
 * Privacy:
 *   • No cookies — sessionStorage only (cleared when tab closes)
 *   • No PII at the event level (we only send user_id if already authenticated)
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${BACKEND}/api/analytics/track`;
const SESSION_KEY = 'acf_sess_id';
const FLUSH_INTERVAL_MS = 4000;
const MAX_BATCH_SIZE = 30;

// ── Session bootstrap ────────────────────────────────────────────────────
function _genSessionId() {
  // 16 random hex chars + timestamp suffix for lexicographic ordering
  const rnd = Math.random().toString(16).slice(2, 18);
  return `${rnd}${Date.now().toString(36)}`;
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = _genSessionId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode or sandbox — fall back to in-memory per-pageload session
    return _genSessionId();
  }
}

// ── Identity (set after login) ───────────────────────────────────────────
let _userId = null;
let _userType = 'guest';

export function setAnalyticsUser({ userId, userType }) {
  _userId = userId || null;
  _userType = userType || 'guest';
}

export function clearAnalyticsUser() {
  _userId = null;
  _userType = 'guest';
}

// ── Buffer + flush ───────────────────────────────────────────────────────
let _buffer = [];
let _flushTimer = null;

function _meta() {
  return {
    screen: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    tz_offset: new Date().getTimezoneOffset(),
    lang: (typeof navigator !== 'undefined' && navigator.language) || '',
  };
}

function _flush(useBeacon = false) {
  if (_buffer.length === 0) return;
  const payload = JSON.stringify({
    events: _buffer,
    ..._meta(),
  });
  _buffer = [];

  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // Beacon requests are guaranteed to be sent even during unload
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    // Fetch with keepalive for reliability without blocking
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch {
    // Never let analytics break the app
  }
}

function _scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush();
  }, FLUSH_INTERVAL_MS);
}

function _enqueue(event) {
  _buffer.push(event);
  if (_buffer.length >= MAX_BATCH_SIZE) {
    _flush();
  } else {
    _scheduleFlush();
  }
}

// ── Public tracking API ──────────────────────────────────────────────────
function _base(path = typeof window !== 'undefined' ? window.location.pathname : '') {
  return {
    session_id: getSessionId(),
    user_id: _userId,
    user_type: _userType,
    path,
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    timestamp: new Date().toISOString(),
  };
}

export function trackPageview(path) {
  _enqueue({ ..._base(path), event_type: 'pageview', data: {} });
}

export function trackSearch(data) {
  // data: { from, to, date, trip_type, adults, children, infants, class, source? }
  _enqueue({ ..._base(), event_type: 'search', data: data || {} });
}

export function trackBookStep(step, data = {}) {
  // step: 'passenger_form' | 'review' | 'payment_init'
  _enqueue({ ..._base(), event_type: 'book_step', data: { step, ...data } });
}

export function trackBookConfirmed(data) {
  // data: { booking_id, source, total_amount, route }
  _enqueue({ ..._base(), event_type: 'book_confirmed', data: data || {} });
  _flush(); // Send immediately — this is a terminal, valuable event
}

// ── Lifecycle hooks (installed once by <AnalyticsProvider />) ────────────
export function installAnalytics() {
  if (typeof window === 'undefined') return;
  if (window.__acfAnalyticsInstalled) return;
  window.__acfAnalyticsInstalled = true;

  // On tab hide/close, flush via beacon
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') _flush(true);
  });
  window.addEventListener('pagehide', () => _flush(true));
  window.addEventListener('beforeunload', () => _flush(true));
}
